import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Grid, Skeleton } from '@mui/material'

import { useGetTagListQuery, useGetTagGroupsListQuery } from '../../../../../api/core'
import { GRBanner } from '../../../../../components/GRBanner/GRBanner'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { TrackedGame } from '../../../../live-events/types/TrackedGame'
import { TrackingEventExample } from '../../../../live-events/types/TrackingEvents'
import { LiveEventTypeCard } from '../LiveEventTypeCard'

type LiveEventsListProps = {
  selectedGames: TrackedGame[]
  filteredEvents?: { [gameId: string]: TrackingEventExample[] }
  isLoading?: boolean
}

export const LiveEventsList: FC<LiveEventsListProps> = ({ selectedGames, filteredEvents, isLoading }) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()

  // Create a map of tracked games by game ID for quick lookup
  const gamesMap = useMemo(() => {
    return selectedGames.reduce((acc, trackedGame) => {
      acc[trackedGame.game.id] = trackedGame
      return acc
    }, {} as { [gameId: string]: TrackedGame })
  }, [selectedGames])

  // Fetch event type tags
  const { data: eventTags, isLoading: tagsLoading } = useGetTagListQuery({ type: 'event', userLanguage })

  // Fetch tag groups for event types
  const { data: tagGroups, isLoading: tagGroupsLoading } = useGetTagGroupsListQuery({ type: 'event', userLanguage })

  // Create a map of tag groups by event type ID
  const tagGroupsMap = useMemo(() => {
    if (!tagGroups) return {}

    const map: { [eventTypeId: string]: any } = {}
    tagGroups.forEach((group) => {
      if (group.tags) {
        group.tags.forEach((tagId) => {
          map[tagId] = group
        })
      }
    })
    return map
  }, [tagGroups])

  // Create a map of event tags by ID
  const eventTagsMap = useMemo(() => {
    if (!eventTags) return {}

    return eventTags.reduce((acc, tag) => {
      acc[tag.id] = tag
      return acc
    }, {} as { [tagId: string]: any })
  }, [eventTags])

  // Group events by typeId
  const eventsByType = useMemo(() => {
    const events = filteredEvents || {}
    if (!events) return {}

    // Use eventId as key to avoid duplicates and track earliest start/latest end
    const grouped: { [typeId: string]: { [eventId: string]: TrackingEventExample } } = {}

    Object.entries(events).forEach(([gameId, gameEvents]) => {
      gameEvents.forEach((event) => {
        if (!grouped[event.typeId]) {
          grouped[event.typeId] = {}
        }

        const existingEvent = grouped[event.typeId][event.eventId]

        if (existingEvent) {
          // Update with earliest start time and latest end time
          grouped[event.typeId][event.eventId] = {
            ...existingEvent,
            start: Math.min(existingEvent.start, event.start),
            end: Math.max(existingEvent.end, event.end),
          }
        } else {
          // First occurrence of this event
          grouped[event.typeId][event.eventId] = event
        }
      })
    })

    // Convert to arrays of unique events
    const result: { [typeId: string]: TrackingEventExample[] } = {}
    Object.entries(grouped).forEach(([typeId, eventsMap]) => {
      result[typeId] = Object.values(eventsMap)
    })

    return result
  }, [filteredEvents])

  // Sort event types alphabetically by name
  const sortedEventTypes = useMemo(() => {
    return Object.entries(eventsByType)
      .sort(([typeIdA], [typeIdB]) => {
        const eventTypeA = eventTagsMap[typeIdA]
        const eventTypeB = eventTagsMap[typeIdB]

        // Handle cases where event type might not be found
        if (!eventTypeA || !eventTypeB) {
          if (!eventTypeA && !eventTypeB) return 0
          if (!eventTypeA) return 1
          return -1
        }

        // Sort alphabetically by name (case-insensitive)
        return eventTypeA.name.localeCompare(eventTypeB.name, undefined, { sensitivity: 'base' })
      })
      .map(([typeId]) => typeId)
  }, [eventsByType, eventTagsMap])

  const isLoadingData = tagsLoading || tagGroupsLoading || isLoading

  if (isLoadingData) {
    return <LoadingSkeleton />
  }

  if (selectedGames.length === 0) {
    return <GRBanner severity="guidance">{t('implementations:no_games_selected_info')}</GRBanner>
  }

  if (sortedEventTypes.length === 0) {
    return <GRBanner severity="guidance">{t('implementations:no_live_events_found')}</GRBanner>
  }

  return (
    <>
      {sortedEventTypes.map((typeId) => {
        const eventType = eventTagsMap[typeId]
        const events = eventsByType[typeId]

        if (!eventType || !events || events.length === 0) {
          return null
        }

        return <LiveEventTypeCard key={typeId} eventType={eventType} events={events} games={gamesMap} tagGroups={tagGroupsMap} />
      })}
    </>
  )
}

const LoadingSkeleton = () => {
  return (
    <Grid container spacing={2} wrap="wrap">
      {Array.from(Array(10).keys()).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={index}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 5 }} />
        </Grid>
      ))}
    </Grid>
  )
}
